<template>
  <div class="labeling-board__wrapper">
    <div class="labeling-board" :class="{'labeling-board--readonly': isReadOnly}" ref="labelingBoard">
    </div>
    <teleport :to="`#before-webrtc-live-video-${patientMonitorId}`" defer>
      <div class="labeling-board__action-buttons" v-if="!isReadOnly">
        <b-select v-if="sensitivityOptions.length" class="labeling-board__select" size="is-small"
          v-model="selectedSensitivity" @update:model-value="setIsChanged(true)">
          <option v-for="opt in sensitivityOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </b-select>
        <button :disabled="!virtualFence" class="button is-danger is-small is-rounded" @click="deleteAction">Delete</button>
        <button class="button is-small is-rounded" @click="cancelAction">Exit</button>
        <button :disabled="!isChanged" class="button is-success is-small is-rounded" @click="saveAction">Save</button>
      </div>
    </teleport>
  </div>
</template>

<script>
import Konva from "konva";
import AnchorableObject from "@/components/labeling/AnchorableObject";
import to from "@/lib/to";
import * as Sentry from "@sentry/vue";
import { configuredSensitivityLevels } from "@/lib/SafetyZoneSensitivity";

export default {
  name: "LabelingBoard",
  props: ['patientMonitorId', 'videoRef', 'isReadOnly', 'digitalPtzScale'],
  emits: ['cancel'],
  data() {
    return {
      /**
       * @type Konva.Stage
       */
      stage: null,
      /**
       * @type Konva.Layer
       */
      layer: null,
      /**
       * @type Konva.Group
       */
      group: null,
      /**
       * @type Konva.Line
       */
      polygon: null,
      previousWidth: null,
      anchorableObject: null,
      virtualFence: null,
      ignore_flag: 'always',
      isChanged: false,
      expirationCheckInterval: null,
      division: null,
      selectedSensitivity: null
    }
  },
  computed: {
    sensitivityOptions() {
      return configuredSensitivityLevels(this.division)
    }
  },
  watch: {
    isReadOnly(newVal) {
      if (newVal) {
        this.destroyConva()
        this.setup()
      }
    },
    '$store.state.currentHospital.safety_zone_expiration'() {
      if (this.isReadOnly && this.virtualFence) {
        this.destroyConva()
        this.setup()
      }
    }
  },
  async mounted() {
    if (!this.$refs.labelingBoard) {
      return;
    }

    if (this.patientMonitorId) {
      const [err, response] = await to(this.$http.get(`/patient_monitors/${this.patientMonitorId}`, { skipLoading: true }))
      if (!err) {
        this.division = response.data.division
        this.virtualFence = response.data.safety_zone
        // fall back to the hospital default, or auto-select when only one level is configured
        const fallbackSensitivity = this.division?.safety_zone_sensitivity_default
          || (this.sensitivityOptions.length === 1 ? this.sensitivityOptions[0].value : null)
        if (this.virtualFence) {
          this.ignore_flag = this.virtualFence.ignore_flag
          this.selectedSensitivity = this.virtualFence.sensitivity || fallbackSensitivity
        } else {
          this.selectedSensitivity = fallbackSensitivity
          this.setIsChanged(true)
        }
      }
    }
    const intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.setup()
        intersectionObserver.disconnect()
      }
    });

    if (this.$refs.labelingBoard) {
      intersectionObserver.observe(this.$refs.labelingBoard)
    }

    if (this.isReadOnly && this.virtualFence) {
      this.expirationCheckInterval = setInterval(() => {
        if (this.polygon && this.isReadOnly) {
          if (this.isExpired()) {
            this.polygon.stroke('#FFC107')
            this.polygon.strokeWidth(3)
          } else {
            this.polygon.stroke('#94B2C0')
            this.polygon.strokeWidth(2)
          }
        }
      }, 60000);
    }
  },
  beforeUnmount() {
    if (this.expirationCheckInterval) {
      clearInterval(this.expirationCheckInterval)
    }
  },
  methods: {
    destroyConva() {
      if (this.stage) {
        this.stage.destroy()
        this.layer.destroy()
        this.group.destroy()
        this.stage = null
        this.layer = null
        this.group = null
      }
    },
    async setup() {

      // in read only mode, if there is no saved fence we don't need to do anything
      if (this.patientMonitorId && !this.virtualFence && this.isReadOnly) {
        return;
      }

      if (!this.$refs.labelingBoard) {
        return;
      }

      this.previousWidth = this.$refs.labelingBoard.offsetWidth
      this.setupCanvas()

      const reCalculatePositions = this.$helpers.debounce(() => {
        if (!this.$refs.labelingBoard) {
          return;
        }

        const [imageWidth, imageHeight] = [this.$refs.labelingBoard.offsetWidth, this.$refs.labelingBoard.offsetHeight]
        if (imageWidth == 0 || imageHeight == 0) {
          return;
        }

        this.stage.width(imageWidth)
        this.stage.height(imageHeight)
        const widthRatio = imageWidth / this.previousWidth
        const line = this.group.findOne('.line')
        if (line) {
          line.setPoints(line.getPoints().map(x => x * widthRatio))
        }
        this.group.x(this.group.x() * widthRatio)
        this.group.y(this.group.y() * widthRatio)
        this.previousWidth = imageWidth
        this.anchorableObject.redraw()
      }, 20)
      const resizeObserver = new ResizeObserver(reCalculatePositions)
      resizeObserver.observe(this.$refs.labelingBoard)
    },
    async deleteAction() {
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want to delete the safety zone?',
        type: 'is-danger',
        confirmText: 'Yes',
        focusOn: 'cancel',
        onConfirm: async () => {
          const [err] = await to(this.$http.delete(`/patient_monitors/${this.patientMonitorId}/safety_zone`))
          if (!err) {
            this.cancelAction()
          }
        }
      })
    },
    cancelAction() {
      this.$emit('cancel')
    },
    async saveAction() {
      const transformer = this.polygon.getAbsoluteTransform()
      const points = this.polygon.getPoints()
      // account for digital ptz CSS scaling
      const scaledWidth = this.videoRef.getBoundingClientRect().width
      const offsetWidth = scaledWidth / (this.digitalPtzScale || 1)
      const payload = {
        scale: this.videoRef.videoWidth / offsetWidth,
        width: this.videoRef.videoWidth,
        height: this.videoRef.videoHeight,
        points: []
      }
      for (let i = 0; i < points.length; i += 2) {
        const [x, y] = points.slice(i, i + 2)
        const p = transformer.point({x, y})
        payload.points.push([p.x, p.y])
      }
      if (payload.points.flat().every(p => Math.floor(p) == 0)) {
        const err = new Error('Safety zone zero coordinates')
        console.error(err)
        Sentry.captureException(err, { tags: { 'error-type': 'virtual-rails-zeroes' } })
        this.$buefy.dialog.alert({
          message: `Can't save safety zone. Please reload the page.`,
        });
        return;
      }
      const [err, response] = await to(this.$http.post(`/patient_monitors/${this.patientMonitorId}/safety_zone`, {
        scale: payload.scale,
        width: payload.width,
        height: payload.height,
        points: payload.points,
        sensitivity: this.selectedSensitivity || null
      }))
      if (!err) {
        this.virtualFence = response.data
        this.setIsChanged(false)
        this.$buefy.toast.open({
          message: `Safety zone ${this.virtualFence ? 'updated' : 'created'} successfully`,
          type: 'is-success'
        })
      }
    },
    setupCanvas() {
      const rect = this.$refs.labelingBoard.getBoundingClientRect()
      this.stage = new Konva.Stage({
        container: this.$refs.labelingBoard,
        width: rect.width,
        height: rect.height,
      });

      this.layer = new Konva.Layer({
        x:0,
        y: 0
      })
      this.stage.add(this.layer)
      this.group = new Konva.Group({
        draggable: !this.isReadOnly,
        x: 0,
        y: 0
      })

      const xPercent = rect.width * 20 / 100;
      const yPercent = rect.height * 25 / 100
      const height = rect.height * 62 / 100;
      const x1 = rect.width / 2 - xPercent, y1 = yPercent,
            x2 = x1, y2 = yPercent + height,
            x3 = y2 + yPercent, y3 = y2,
            x4 = x3, y4 = y1;

      let points = [x1, y1, x2, y2, x3, y3, x4, y4]
      if (this.virtualFence) {
        const newScale = this.previousWidth / this.virtualFence.width
        points = this.virtualFence.points.flat().map(x => x * this.virtualFence.scale * newScale)
      }
      const lineConfig = {
        points,
        closed: true,
        hitStrokeWidth: 40,
        name: 'line',
        strokeWidth: 3,
        stroke: 'red',
        x: 0,
        y: 0,
        strokeScaleEnabled: false,
      }

      if (this.isReadOnly) {
        if (this.virtualFence && this.isExpired()) {
          lineConfig.strokeWidth = 3
          lineConfig.stroke = '#FFC107'
        } else {
          lineConfig.strokeWidth = 2
          lineConfig.stroke = '#94B2C0'
        }
      }

      this.polygon = new Konva.Line(lineConfig)
      this.anchorableObject = new AnchorableObject(this.layer, this.polygon, this.group, this.isReadOnly)
      this.anchorableObject.init()
      this.anchorableObject.onchange = () => this.setIsChanged(true)
      this.group.on('dragmove', () => this.setIsChanged(true))
      this.group.add(this.polygon)
      this.layer.add(this.group)
    },
    setIsChanged(state) {
      this.isChanged = state
    },
    isExpired() {
      if (!this.virtualFence || !this.virtualFence.created_at) {
        return false
      }
      
      const expirationHours = this.$store.state.currentHospital?.safety_zone_expiration
      const createdAt = new Date(this.virtualFence.created_at)
      const expirationTime = new Date(createdAt.getTime() + (expirationHours * 60 * 60 * 1000))
      const currentTime = new Date()
      
      return currentTime > expirationTime
    }
  }
}
</script>

<style scoped lang="scss">
  .labeling-board {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 99;
    top: 0;
    left: 0;
    &.labeling-board--readonly {
      z-index: 9;
    }
  }
  .labeling-board__wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  }
  .labeling-board__action-buttons {
    width: 80px;
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 999;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    > * {
      width: 100%;
    }
    > button {
      margin-bottom: 5px;
    }
  }
  .labeling-board__select {
    width: 100%;
    margin-bottom: 5px;
    :deep(.select) {
      width: 100%;
      height: auto;
      &:not(.is-multiple):not(.is-loading)::after {
        right: 0.9em;
        height: 0.45em;
        width: 0.45em;
        top: 45%;
      }
      select {
        width: 100%;
        min-height: 0;
        height: auto;
        border-radius: 9999px;
        font-size: 0.75rem;
        line-height: 1.5;
        padding: 4px 2em 4px 1em;
      }
    }
  }
</style>
